import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { AuthContext } from "../../context/auth-context/AuthContext";

import {
  getAllEvents,
  getEventsByMemberId,
  signUpForEvent,
  signOutFromEvent,
  createEvent,
  signUpForWorkingWeekend,
} from "../../api/events";
import { authReducerActionType } from "../../types/globalTypes";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";
import { AxiosError } from "axios";
import { CreateEventDto, WWRegistrationDto } from "../../types/eventTypes";

/**
 * Hook that handles the events api calls, uses react-query
 * @returns The events, a function to get event details, and functions to create, update, and delete events
 */
const useEvents = () => {
  const { dispatchAuth } = useContext(AuthContext);
  const queryClient = useQueryClient();

  // ############
  // GET QUERIES
  // ############

  // getEvents query
  const {
    data: eventsData,
    isLoading: isEventsLoading,
    isError: isEventsError,
  } = useQuery({
    queryKey: ["events"],
    queryFn: getAllEvents,
  });

  const allEvents = eventsData?.data || [];

  // getEventsByMemberId query
  const { data: eventsByMemberData } = useQuery({
    queryKey: ["userEvents"],
    queryFn: getEventsByMemberId,
  });

  const userEvents = eventsByMemberData?.data || [];

  // ----------------------------------------------------------------------------------
  // ############
  // CREATE QUERIES
  // ############

  // createEvent mutation
  const { mutate: createEventMutation } = useMutation({
    mutationFn: createEvent,
    onSuccess: () => {
      showSuccessMessage("Event erfolgreich erstellt");
      // Invalidate and refetch events query to get the updated data
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
    onError: (err: AxiosError) => {
      showErrorMessage("Erstellen des Events fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
  });

  /**
   * Creates a new event with the given `event`
   * @param event The event to create
   */
  const createNewEvent = async (event: CreateEventDto) => {
    createEventMutation(event);
  };

  // signUpForEvent mutation
  const { mutate: signUpForEventMutatation } = useMutation({
    mutationFn: ({ eventId, participantMemberId }: { eventId: number; participantMemberId: number }) =>
      signUpForEvent(eventId, participantMemberId),
    onSuccess: () => {
      showSuccessMessage("Erfolgreich für Event angemeldet");
      // Invalidate and refetch events and userEvents queries to get the updated data
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["userEvents"] });
      queryClient.invalidateQueries({ queryKey: ["eventDetails"] });
    },
    onError: (err: AxiosError) => {
      showErrorMessage("Anmeldung für Event fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
  });

  const signUserUpForEvent = async (eventId: number, participantMemberId: number) => {
    signUpForEventMutatation({ eventId, participantMemberId });
  };

  // signUpForWorkingWeekend mutation
  const { mutateAsync: signUpForWorkingWeekendMutation } = useMutation({
    mutationFn: ({
      eventId,
      memberId,
      wwRegistration,
    }: {
      eventId: number;
      memberId: number;
      wwRegistration: WWRegistrationDto;
    }) => signUpForWorkingWeekend(eventId, memberId, wwRegistration),
    onSuccess: () => {
      showSuccessMessage("Erfolgreich für Working Weekend angemeldet");
      // Invalidate and refetch events and userEvents queries to get the updated data
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["userEvents"] });
      queryClient.invalidateQueries({ queryKey: ["eventDetails"] });
    },
    onError: (err: AxiosError) => {
      showErrorMessage("Anmeldung für Working Weekend fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
  });

  const signUserUpForWorkingWeekend = async (eventId: number, memberId: number, wwRegistration: WWRegistrationDto) => {
    await signUpForWorkingWeekendMutation({ eventId, memberId, wwRegistration });
  };

  // ----------------------------------------------------------------------------------
  // ############
  // DELETE QUERIES
  // ############

  // signOutFromEvent mutation
  const { mutate: signOutFromEventMutation } = useMutation({
    mutationFn: ({ eventId, participantMemberId }: { eventId: number; participantMemberId: number }) =>
      signOutFromEvent(eventId, participantMemberId),
    onSuccess: () => {
      showSuccessMessage("Erfolgreich von Event abgemeldet");
      // Invalidate and refetch events and userEvents queries to get the updated data
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["userEvents"] });
      queryClient.invalidateQueries({ queryKey: ["eventDetails"] });
    },
    onError: (err: AxiosError) => {
      showErrorMessage("Abmeldung von Event fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
  });

  const signUserOutFromEvent = async (eventId: number, participantMemberId: number) => {
    signOutFromEventMutation({ eventId, participantMemberId });
  };

  return {
    // Data
    allEvents,
    isEventsLoading,
    isEventsError,
    userEvents,
    createNewEvent,
    signUserUpForEvent,
    signUserOutFromEvent,
    signUserUpForWorkingWeekend,
  };
};

export default useEvents;
